import React from 'react';

const iconSize = 18;

export const LifeAnalysisIcons = {
  mood: (
    <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <line x1="9" y1="9" x2="9.01" y2="9" />
      <line x1="15" y1="9" x2="15.01" y2="9" />
    </svg>
  ),
  triggers: (
    <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  avoidance: (
    <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
    </svg>
  ),
  roots: (
    <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="life-analysis-roots-icon">
      {/* Tiny trunk */}
      <line x1="12" y1="2" x2="12" y2="8" />
      {/* Roots spreading down and apart */}
      <path d="M12 8 Q3 14 2 22" />
      <path d="M12 8 Q7 16 6 22" />
      <path d="M12 8 L12 22" />
      <path d="M12 8 Q17 16 18 22" />
      <path d="M12 8 Q21 14 22 22" />
    </svg>
  ),
  topics: (
    <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      <line x1="8" y1="7" x2="16" y2="7" />
      <line x1="8" y1="11" x2="16" y2="11" />
    </svg>
  ),
  attachment: (
    <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
};

/** Structured content for Attachment section info tooltip (title, headers, body, disclaimer) */
export const ATTACHMENT_OVERVIEW = [
  { type: 'title', text: 'Attachment Styles Overview' },
  { type: 'header', text: 'Secure attachment' },
  { type: 'body', text: 'People with a secure attachment style generally feel comfortable with closeness and independence. They trust others, communicate their needs calmly, and handle conflict without excessive fear or withdrawal. They can rely on others when needed and also function well on their own.' },
  { type: 'header', text: 'Anxious attachment' },
  { type: 'body', text: 'People with an anxious attachment style often worry about being abandoned or not being valued enough in relationships. They may seek frequent reassurance, overthink small signals, or feel distressed when they perceive distance from others. Their focus is usually on maintaining closeness and avoiding rejection.' },
  { type: 'header', text: 'Avoidant attachment' },
  { type: 'body', text: 'People with an avoidant attachment style tend to feel uncomfortable with emotional closeness or dependence. They may prefer handling problems on their own, minimize emotional needs, or withdraw during conflict. Their focus is usually on maintaining independence and emotional distance.' },
  { type: 'header', text: 'Fearful-avoidant (mixed) attachment' },
  { type: 'body', text: 'People with a fearful-avoidant style show both anxious and avoidant tendencies. They may desire closeness but also fear it, leading to push-pull patterns in relationships. At times they seek connection, and at other times they distance themselves to feel safe.' },
  { type: 'header', text: 'Secure–anxious' },
  { type: 'body', text: 'Some people are generally secure but show anxious tendencies in certain situations or relationships. They may usually communicate calmly and trust others, yet become worried about losing closeness during stressful periods. This pattern reflects a mostly secure base with occasional fears of abandonment.' },
  { type: 'header', text: 'Secure–avoidant' },
  { type: 'body', text: 'Some people are generally secure but display avoidant tendencies in specific contexts. They may be comfortable with trust and communication overall, yet prefer distance or independence when emotions become intense. This reflects a secure foundation with occasional discomfort around closeness.' },
  { type: 'header', text: 'Disclaimer' },
  { type: 'disclaimer', text: 'Attachment styles shown here are general psychological patterns for self-reflection and educational purposes only. They are not a clinical diagnosis and may vary across situations, relationships, and life stages. For an accurate assessment, consult a qualified mental health professional.' },
];

/** Structured content for Triggers section info tooltip */
export const TRIGGERS_OVERVIEW = [
  { type: 'title', text: 'What Is a Trigger?' },
  { type: 'body', text: 'A trigger is something outside of you that quickly sets off a strong emotional reaction. It can make you feel angry, anxious, defensive, hurt, or overwhelmed — sometimes before you even think about it.' },
  { type: 'header', text: 'Triggers are usually linked to things that matter deeply to you:' },
  { type: 'list', items: ['Feeling respected', 'Feeling included', 'Feeling in control', 'Feeling safe', 'Feeling understood'] },
  { type: 'header', text: 'When one of these feels threatened, your body reacts fast. That reaction can show up as:' },
  { type: 'list', items: ['Irritation', 'Fighting or arguing', 'Shutting down', 'Overthinking', 'Avoiding someone', 'Feeling tense or restless'] },
  { type: 'header', text: 'Triggers are not weaknesses.' },
  { type: 'body', text: 'They are signals. They show you where something feels important, sensitive, or unresolved.' },
  { type: 'header', text: 'Understanding your triggers helps you:' },
  { type: 'list', items: ['Pause before reacting', 'Communicate more clearly', 'Notice patterns', 'Respond instead of react'] },
  { type: 'header', text: 'Disclaimer' },
  { type: 'disclaimer', text: 'This trigger chart is for self-reflection only. It is not a psychological diagnosis and does not replace professional advice. Everyone\'s emotional patterns are unique, and deeper issues may require support from a qualified professional.' },
];

/** Structured content for Roots section info tooltip */
export const ROOTS_OVERVIEW = [
  { type: 'title', text: 'What Are "Roots" in This Chart?' },
  { type: 'body', text: 'Roots are the early life influences that shape how you see yourself, others, and the world. They usually come from childhood and teenage years.' },
  { type: 'body', text: 'They include family patterns, home environment, money situation, religion, culture, school experiences, and major life events.' },
  { type: 'body', text: 'These roots form the core beliefs that quietly guide your reactions, choices, and relationships today. Understanding your roots helps you see where certain thoughts and patterns originally began.' },
  { type: 'header', text: 'Disclaimer' },
  { type: 'disclaimer', text: 'This chart is for self-reflection and personal insight only. It is not a psychological diagnosis and does not replace professional advice or therapy.' },
];

/** Structured content for Topics section info tooltip */
export const TOPICS_OVERVIEW = [
  { type: 'title', text: 'Topics Overview' },
  { type: 'body', text: 'Topics are extracted from your reflections to show what you discuss most. Categories are for self-reflection only.' },
  { type: 'header', text: 'Disclaimer' },
  { type: 'disclaimer', text: 'Topic categories reflect patterns in your reflections and are not a clinical assessment.' },
];

export const LIFE_ANALYSIS_SECTIONS = [
  { id: 'mood', label: 'Moods', isChart: true, infoTooltip: 'This chart shows your daily moods over the last 30 days. Moods move like a wave; the trend line shows how your mood has been changing recently.' },
  { id: 'topics', label: 'Topics', isChart: true, infoOverview: true },
  { id: 'triggers', label: 'Triggers', isChart: true, infoOverview: true },
  { id: 'attachment', label: 'Attachment style', isChart: true, infoOverview: true },
  { id: 'roots', label: 'Roots', isChart: true, infoOverview: true },
  { id: 'avoidance', label: 'Avoidance heat map', isChart: false },
];
